import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaWhatsapp } from 'react-icons/fa';

const links = [
  { label: 'INICIO', to: '#hero' },
  { label: 'NOSOTROS', to: '#about' },
  { label: 'PRODUCTOS', to: '#products' },
  { label: 'CLIENTES', to: '#clients' },
  { label: 'CONTACTO', to: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState('#hero');

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const sections = links.map((l) => document.querySelector(l.to));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive('#' + e.target.id);
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    sections.forEach((s) => s && observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Nav>
      <Bar $scrolled={scrolled}>
        <Inner>
          <Logo onClick={() => scrollTo('#hero')}>
            <img src="/img/logo/grupojaycom_logo.png" alt="Grupo Jaycom Tarimas" />
          </Logo>

          <Links>
            {links.map((l) => (
              <NavLink
                key={l.label}
                onClick={() => scrollTo(l.to)}
                $active={active === l.to}
              >
                {l.label}
                {active === l.to && (
                  <ActiveDot
                    as={motion.span}
                    layoutId="activeDot"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </NavLink>
            ))}
          </Links>

          <Right>
            <CTABtn href="https://wa.me/524311011283?text=Hola%2C%20los%20contacto%20desde%20su%20sitio%20web." target="_blank" rel="noopener">
              <FaWhatsapp size={15} />
              COTIZAR
            </CTABtn>
            <Burger onClick={() => setMenuOpen((p) => !p)}>
              {menuOpen ? <FaTimes /> : <FaBars />}
            </Burger>
          </Right>
        </Inner>
      </Bar>

      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            as={motion.div}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {links.map((l, i) => (
              <motion.div
                key={l.label}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <MobileLink onClick={() => scrollTo(l.to)} $active={active === l.to}>
                  <MobileNum>0{i + 1}</MobileNum>
                  {l.label}
                </MobileLink>
              </motion.div>
            ))}
            <MobileCTA href="https://wa.me/524311011283?text=Hola%2C%20los%20contacto%20desde%20su%20sitio%20web." target="_blank" rel="noopener">
              <FaWhatsapp size={16} />
              SOLICITAR COTIZACIÓN
            </MobileCTA>
          </MobileMenu>
        )}
      </AnimatePresence>
    </Nav>
  );
}

const Nav = styled.nav`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1000;
`;

const Bar = styled.div`
  transition: background 0.35s, box-shadow 0.35s;

  ${({ $scrolled }) => $scrolled ? css`
    background: rgba(255,255,255,0.97);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: 0 1px 0 var(--border), 0 4px 24px rgba(0,0,0,0.06);
  ` : css`
    background: transparent;
  `}
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
`;

const Logo = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 20px 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  img {
    height: 120px;
    width: auto;
    display: block;
  }
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: 0.1rem;
  @media (max-width: 900px) { display: none; }
`;

const NavLink = styled.button`
  position: relative;
  background: none;
  border: none;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.05rem;
  letter-spacing: 0.12em;
  cursor: pointer;
  padding: 0.55rem 1rem 0.7rem;
  color: ${({ $active }) => $active ? 'var(--red)' : 'var(--text)'};
  transition: color 0.2s;

  &:hover { color: var(--red); }
`;

const ActiveDot = styled.span`
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--red);
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
`;

const CTABtn = styled.a`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  background: var(--red);
  color: #fff;
  padding: 0.6rem 1.4rem;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1rem;
  letter-spacing: 0.12em;
  transition: background 0.2s, transform 0.2s;
  &:hover { background: var(--red-dk); transform: translateY(-1px); }

  @media (max-width: 900px) { display: none; }
`;

const Burger = styled.button`
  background: none;
  border: none;
  font-size: 1.3rem;
  cursor: pointer;
  color: var(--text);
  display: none;
  padding: 0.3rem;
  @media (max-width: 900px) { display: block; }
`;

const MobileMenu = styled.div`
  background: #fff;
  border-top: 3px solid var(--red);
  padding: 1.5rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  box-shadow: 0 16px 40px rgba(0,0,0,0.1);
`;

const MobileLink = styled.button`
  background: none;
  border: none;
  border-bottom: 1px solid var(--border);
  text-align: left;
  cursor: pointer;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 2rem;
  letter-spacing: 0.08em;
  color: ${({ $active }) => $active ? 'var(--red)' : 'var(--text)'};
  padding: 0.85rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: color 0.2s, padding-left 0.2s;
  width: 100%;

  &:hover { color: var(--red); padding-left: 0.4rem; }
  &:last-of-type { border-bottom: none; }
`;

const MobileNum = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: var(--red);
  opacity: 0.55;
  min-width: 24px;
`;

const MobileCTA = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  background: var(--red);
  color: #fff;
  padding: 1rem 1.5rem;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.15rem;
  letter-spacing: 0.12em;
  transition: background 0.2s;
  &:hover { background: var(--red-dk); }
`;
